import { ref as dbRef, get, onValue, set, update } from 'firebase/database';
import { db, auth } from '../firebase';
import { compressImage, IMAGE_PRESETS, userFacingError } from './imageCompressionUtil';
// One-way: posUtil speaks for a single client's trading data and knows nothing
// about the registry, so importing it here creates no cycle. The VAT setting is
// written from /admin, but what it changes is what that CLIENT charges — so its
// audit entry belongs on the client's own trail, where the staff who noticed
// the totals move will actually go looking for it.
import { logPosEvent, posVatMode, posVatRate, posReserveEnabled } from './posUtil';
import {
  POS_CLIENTS_NODE,
  POS_AUDIT_ACTIONS,
  POS_VAT_MODE_NONE,
  POS_VAT_MODE_KEYS,
  POS_VAT_RATE_MAX,
  POS_STAFF_EMAIL_TLD,
  POS_LOW_STOCK_DEFAULT,
  POS_LOW_STOCK_MAX,
  POS_GLOBAL_CONFIG_NODE,
  POS_ADMIN_EMAILS,
  POS_SUPERUSER_EMAILS,
  POS_RESERVED_CLIENT_IDS,
  POS_CLIENT_ID_PATTERN,
  POS_CLIENT_ID_MAX_LENGTH,
  POS_ROLES_NODE,
  POS_PERMISSION_KEYS,
  POS_ROLE_NAME_MAX_LENGTH,
  POS_UNASSIGNED_STAFF_UNRESTRICTED,
  POS_DEFAULT_TIMEZONE,
  POS_TENANTS_NODE,
  POS_ROLE_OVERRIDES_NODE,
  POS_CONFIG_NODE,
  POS_RESERVE_ENABLED_KEY,
} from '../constants/posConst';
import { isValidTimeZone } from './subscriptionDateUtil';
import { isStoreAdminRole } from './roleUtil';

// The client registry: who the POS is running for, and who may stand at each
// till. Kept apart from posUtil.js on purpose — that module speaks for ONE
// client's trading data, this one describes the estate around it.
//
// SHAPE. Each client is split in two on purpose:
//
//   posClients/<clientId>/profile   name, logo, email, contact
//   posClients/<clientId>/staff     { <emailKey>: true }
//
// The profile is readable by anyone signed in, because the till has to show a
// club's name and logo on its sign-in card BEFORE anyone has signed in. The
// staff list is not: it is a list of real people's email addresses, and an
// admin-only read keeps it from being downloaded wholesale by anyone holding
// a staff login. A member of staff can still check their own membership,
// which needs one exact key rather than the list.

/**
 * A Firebase key for an email address.
 *
 * RTDB keys cannot contain . # $ [ ] or /, and an email contains at least one
 * dot. Lower-cased first so Staff@club.com and staff@club.com are one person —
 * Firebase Auth echoes back whatever casing the account was created with.
 *
 * @param {string} email
 * @returns {string} '' for a blank or unusable address.
 */
export const emailKey = (email) => String(email || '')
  .trim()
  .toLowerCase()
  .replace(/[.#$[\]/]/g, ',');

/**
 * Whether an address is one of the POS administrators.
 *
 * Mirrored by database.rules.json, which checks the same two addresses
 * against auth.token.email. This copy is what keeps the admin screens from
 * rendering; that copy is what actually protects the data.
 *
 * @param {string} email
 * @returns {boolean}
 */
export const isPosAdmin = (email) => POS_ADMIN_EMAILS
  .map((a) => a.toLowerCase())
  .includes(String(email || '').trim().toLowerCase());

/**
 * Whether an address may open ANY client's till without being on its staff
 * list.
 *
 * Narrower than isPosAdmin on purpose: every superuser is an administrator,
 * but not every administrator is a superuser. Opening /admin and serving
 * customers at a particular counter are different permissions, so they are
 * different lists.
 *
 * @param {string} email
 * @returns {boolean}
 */
export const isPosSuperuser = (email) => POS_SUPERUSER_EMAILS
  .map((a) => a.toLowerCase())
  .includes(String(email || '').trim().toLowerCase());

/** The signed-in Firebase account's email, or ''. @returns {string} */
export const currentEmail = () => auth?.currentUser?.email || '';

/**
 * Checks a client id before it is written.
 *
 * Validated rather than sanitised: an id silently rewritten from what the
 * admin typed would still become that client's permanent URL, and they would
 * find out from a broken link rather than from this form.
 *
 * @param {string} value
 * @returns {string} the trimmed, lower-cased id
 * @throws {Error} user-facing, naming the specific problem
 */
export const validateClientId = (value) => {
  const id = String(value || '').trim().toLowerCase();
  if (!id) throw userFacingError('Enter a client id.');
  if (id.length > POS_CLIENT_ID_MAX_LENGTH) {
    throw userFacingError(`A client id can be at most ${POS_CLIENT_ID_MAX_LENGTH} characters.`);
  }
  if (!POS_CLIENT_ID_PATTERN.test(id)) {
    throw userFacingError('A client id may use lower-case letters, numbers and single hyphens only — it becomes the /<id> address.');
  }
  if (POS_RESERVED_CLIENT_IDS.includes(id)) {
    throw userFacingError(`"${id}" is reserved and cannot be used as a client id.`);
  }
  return id;
};

/**
 * Suggests a client id from a club's name, for the registration form.
 *
 * Only ever a suggestion the admin can overwrite — see validateClientId.
 *
 * @param {string} name
 * @returns {string}
 */
export const suggestClientId = (name) => String(name || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, POS_CLIENT_ID_MAX_LENGTH);

/**
 * The email domain SUGGESTED for a client's staff addresses.
 *
 * Derived from the client id rather than stored, so it cannot drift from the
 * client it belongs to: client `paddlegrip` suggests `@paddlegrip.com`. Used
 * for the placeholder and the hint in the registration form; nothing rejects
 * an address for being outside it.
 *
 * @param {string} clientId
 * @returns {string} e.g. 'paddlegrip.com'
 */
export const clientEmailDomain = (clientId) => `${String(clientId || '').trim().toLowerCase()}.${POS_STAFF_EMAIL_TLD}`;

/**
 * Checks one staff address.
 *
 * Only that it looks like an address. The per-client domain above is a
 * SUGGESTION shown in the form, not a rule enforced here: clubs have their own
 * real domains, and refusing a working address because it does not match a
 * convention would be the tool arguing with reality.
 *
 * Collisions between clients are therefore caught by findStaffEmailConflicts
 * rather than prevented by the naming scheme — which is why that check runs on
 * every save rather than being treated as a backstop.
 *
 * @param {string} clientId - accepted for symmetry with the rest of this
 *   module and so a per-client rule could be reinstated in one place.
 * @param {string} email
 * @returns {string} the trimmed, lower-cased address
 * @throws {Error} user-facing, naming the specific problem
 */
// eslint-disable-next-line no-unused-vars
export const validateStaffEmail = (clientId, email) => {
  const address = String(email || '').trim().toLowerCase();

  if (!address) throw userFacingError('Enter a staff email address, or remove the empty row.');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
    throw userFacingError(`"${address}" is not a valid email address.`);
  }
  return address;
};

// ---- Registry -------------------------------------------------------------

/**
 * Live list of every registered client, id included, sorted by name.
 *
 * Admin-only in the rules: this returns the whole estate.
 *
 * A FAILED read goes to `onError`, never to `callback` as an empty list. The
 * two were conflated once and it was actively misleading: a refused read —
 * rules not deployed, or an account the rules do not accept as an
 * administrator — rendered as "No clients registered yet", which sends
 * whoever is looking to register a client that already exists rather than to
 * the permission problem in front of them.
 *
 * @param {(clients: object[]) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribePosClients = (callback, onError) => onValue(
  dbRef(db, POS_CLIENTS_NODE),
  (snapshot) => {
    const out = [];
    snapshot.forEach((child) => {
      const value = child.val() || {};
      out.push({
        id: child.key,
        ...(value.profile || {}),
        staffCount: Object.keys(value.staff || {}).length,
        // Carried on the card so the list can say where each client's plan
        // stands without a read per client.
        subscription: value.subscription || null,
        // Present only while an administrator has disabled the till.
        access: value.access || null,
      });
    });
    out.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    callback(out);
  },
  (error) => {
    console.error('POS clients listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * Live profile of ONE client — what the till needs to brand itself.
 *
 * `null` through `onProfile` means the read SUCCEEDED and there is no such
 * client: the till renders a "not found" screen, since a mistyped /<id>
 * would otherwise look like a working but empty counter.
 *
 * A FAILED read is reported through `onError` instead, and never as null.
 * The two were conflated once and it showed: signing out cancels this listener
 * with permission_denied for the moment before the shared account is restored,
 * and the till answered a perfectly valid address with "no till here" — then
 * stayed there, because a cancelled listener does not reopen by itself. The
 * caller re-subscribes on an auth change; this only has to stop lying about
 * which of the two happened.
 *
 * @param {string} clientId
 * @param {(profile: object|null) => void} onProfile
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribePosClientProfile = (clientId, onProfile, onError) => onValue(
  dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/profile`),
  (snapshot) => onProfile(snapshot.exists() ? { id: clientId, ...snapshot.val() } : null),
  (error) => {
    console.error('POS client profile listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * Checks a client's VAT settings and returns them in the shape the profile
 * stores, throwing a message an administrator can act on for anything else.
 *
 * THE RATE IS FORCED TO 0 WHEN VAT IS OFF, rather than kept "in case it is
 * turned back on". A stored rate that nothing is charging is a figure someone
 * will later read as the one in force; posVatBreakdown ignores it either way,
 * so keeping it buys nothing and misleads whoever opens the database.
 *
 * @param {{vatMode?: string, vatRate?: number|string}} settings
 * @returns {{vatMode: string, vatRate: number}}
 * @throws {Error} user-facing, on an unknown mode or an out-of-range rate
 */
const validateVatSettings = ({ vatMode, vatRate }) => {
  const mode = String(vatMode || POS_VAT_MODE_NONE).trim();
  if (!POS_VAT_MODE_KEYS.includes(mode)) {
    throw userFacingError('Choose one of the VAT options before saving.');
  }
  if (mode === POS_VAT_MODE_NONE) return { vatMode: mode, vatRate: 0 };

  // Blank is rejected rather than defaulted: "VAT Inclusive at nothing per
  // cent" is a setting someone half-finished, and quietly completing it for
  // them is how a club ends up charging a rate it never chose.
  const rate = Number(String(vatRate ?? '').trim());
  if (!Number.isFinite(rate) || rate <= 0 || rate > POS_VAT_RATE_MAX) {
    throw userFacingError(`Enter the VAT rate as a percentage above 0 and no more than ${POS_VAT_RATE_MAX}, or set VAT to "No VAT".`);
  }
  // Two decimal places is as fine as any published rate gets, and it keeps the
  // stored figure from carrying dust a pasted value might bring with it.
  return { vatMode: mode, vatRate: Math.round(rate * 100) / 100 };
};

/**
 * Registers a new client, or updates an existing one.
 *
 * The id is immutable once set: it is the client's URL and the key its whole
 * trading subtree hangs from, so changing it would orphan both. Editing an
 * existing client therefore writes the profile only.
 *
 * The TIMEZONE is the one every subscription date for this client is counted
 * in — see utils/subscriptionDateUtil.js. Defaults to the Philippines.
 *
 * @param {{clientId: string, name: string, email: string, contact: string, logoFile: File|null, vatMode?: string, vatRate?: number|string, timezone?: string, existing: object|null, actor?: string}} details
 * @returns {Promise<string>} the client id
 */
export const savePosClient = async ({
  clientId, name, email, contact, logoFile,
  vatMode = POS_VAT_MODE_NONE, vatRate = 0,
  timezone = POS_DEFAULT_TIMEZONE,
  existing = null, actor = '',
}) => {
  const id = validateClientId(clientId);
  const zone = String(timezone || '').trim() || POS_DEFAULT_TIMEZONE;
  if (!isValidTimeZone(zone)) {
    throw userFacingError(`"${zone}" is not a timezone this browser recognises. Pick one from the list.`);
  }
  const clean = {
    name: String(name || '').trim(),
    email: String(email || '').trim(),
    contact: String(contact || '').trim(),
    timezone: zone,
    ...validateVatSettings({ vatMode, vatRate }),
  };
  if (!clean.name) throw userFacingError('Enter the client name.');
  // Deliberately loose — one @ with something either side. A strict pattern
  // rejects addresses that are perfectly valid, and this field is a contact
  // detail rather than a login.
  if (clean.email && !/^[^\s@]+@[^\s@]+$/.test(clean.email)) {
    throw userFacingError('Enter a valid client email address, or leave it blank.');
  }

  if (!existing) {
    // Checked rather than blindly written: `set` on an existing client would
    // replace its profile, and a duplicate id would quietly hand a second club
    // the first one's till.
    const taken = await get(dbRef(db, `${POS_CLIENTS_NODE}/${id}/profile`));
    if (taken.exists()) {
      throw userFacingError(`"${id}" is already registered. Pick a different client id.`);
    }
  }

  let logoUrl = existing?.logoUrl || null;
  if (logoFile) {
    // LOGO preset, not PHOTO: this renders at header and sign-in-card size,
    // and it is downloaded by every till session for that client.
    const { dataUrl } = await compressImage(logoFile, IMAGE_PRESETS.LOGO);
    logoUrl = dataUrl;
  }

  const profile = {
    ...clean,
    logoUrl,
    createdAt: existing?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // update() rather than set() on the client node: set would wipe the `staff`
  // sibling, quietly locking every member of staff out of a client whose phone
  // number was corrected.
  await update(dbRef(db, `${POS_CLIENTS_NODE}/${id}`), { profile });

  // Recorded only when the tax actually MOVED. This form saves the whole
  // profile every time, so logging unconditionally would put a "VAT settings
  // updated" line on the trail for a corrected phone number — and a trail that
  // reports changes that did not happen is one nobody reads.
  const vatChanged = existing
    ? (posVatMode(existing) !== profile.vatMode || posVatRate(existing) !== profile.vatRate)
    : profile.vatMode !== POS_VAT_MODE_NONE;

  if (vatChanged) {
    logPosEvent({
      clientId: id,
      actor,
      action: POS_AUDIT_ACTIONS.VAT_UPDATED,
      entityId: id,
      summary: {
        mode: profile.vatMode,
        rate: profile.vatRate,
        ...(existing ? { before: { mode: posVatMode(existing), rate: posVatRate(existing) } } : {}),
      },
    });
  }

  return id;
};

// ---- Feature settings ------------------------------------------------------
//
// Switches that turn whole parts of the till on or off for one client. Set by
// an administrator from the client form, not by the club's own store admin:
// they change what the club is getting, not how it likes things laid out.
//
// STORED IN THE CLIENT'S OWN CONFIG NODE (posTenants/<id>/config) rather than
// on the profile, because that is where the till already reads them from via
// ConfigContext — moving the switch to /admin should not move every reader.
// The rules let only an administrator change these keys there.

/**
 * The feature switches for one client, as stored.
 *
 * Read as ONE value rather than the whole config node: the node also holds
 * the club's theme colours, which this form has no business downloading.
 *
 * @param {string} clientId
 * @returns {Promise<{reserveEnabled: boolean}>} absent reads as on, matching
 *   posReserveEnabled, so an older client shows what its till is doing.
 */
export const fetchClientFeatureSettings = async (clientId) => {
  const snap = await get(dbRef(db, `${POS_TENANTS_NODE}/${clientId}/${POS_CONFIG_NODE}/${POS_RESERVE_ENABLED_KEY}`));
  return {
    [POS_RESERVE_ENABLED_KEY]: posReserveEnabled({ [POS_RESERVE_ENABLED_KEY]: snap.val() }),
  };
};

/**
 * Saves one client's feature switches, and records the change on that
 * client's own trail.
 *
 * WRITTEN ONLY WHEN SOMETHING MOVED. The client form saves every tab at once,
 * so writing unconditionally would put a "feature settings changed" line on
 * the club's trail for a corrected phone number.
 *
 * @param {string} clientId
 * @param {{reserveEnabled: boolean}} settings
 * @param {{existing?: {reserveEnabled: boolean}|null, actor?: string}} [options]
 *   existing is what was stored when the form opened; null for a new client,
 *   whose absent value already reads as on.
 * @returns {Promise<boolean>} whether anything was written
 * @throws {Error} user-facing, when the reserve switch is not a yes/no value
 */
export const saveClientFeatureSettings = async (clientId, settings, { existing = null, actor = '' } = {}) => {
  const reserveOn = settings?.[POS_RESERVE_ENABLED_KEY];
  if (typeof reserveOn !== 'boolean') {
    throw userFacingError('Choose whether this client keeps a reserve (storeroom) stock.');
  }
  // A new client with no stored value already reads as on.
  const before = existing ? existing[POS_RESERVE_ENABLED_KEY] : true;
  if (before === reserveOn) return false;

  await update(dbRef(db, `${POS_TENANTS_NODE}/${clientId}/${POS_CONFIG_NODE}`), {
    [POS_RESERVE_ENABLED_KEY]: reserveOn,
  });

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.FEATURES_UPDATED,
    entityId: POS_CONFIG_NODE,
    summary: {
      reserveStock: reserveOn ? 'on' : 'off',
      before: { reserveStock: before ? 'on' : 'off' },
    },
  });
  return true;
};

// ---- Staff ----------------------------------------------------------------

/**
 * The staff allowed to open one client's till, and the role each one holds.
 * Admin-only read.
 *
 * `roleId` is '' for an account with no role — which is every account written
 * before roles existed. What that means at the till is decided in one place,
 * by POS_UNASSIGNED_STAFF_UNRESTRICTED; this just reports the absence.
 *
 * @param {string} clientId
 * @returns {Promise<Array<{email: string, roleId: string}>>} sorted by address
 */
export const fetchClientStaff = async (clientId) => {
  const snapshot = await get(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/staff`));
  const value = snapshot.val() || {};
  return Object.values(value)
    .map((entry) => ({
      email: String(entry?.email || '').trim().toLowerCase(),
      roleId: String(entry?.roleId || '').trim(),
    }))
    .filter((entry) => entry.email)
    .sort((a, b) => a.email.localeCompare(b.email));
};

/**
 * Every staff address already claimed by a DIFFERENT client.
 *
 * Reads the whole registry, which only an administrator can do — and this only
 * ever runs on an admin screen. Returns the owner alongside each address so
 * the error can say where the conflict is rather than just that there is one.
 *
 * This is the ONLY thing standing between two clients and a shared address —
 * the per-client domain is a suggestion, not a rule — so it runs on every
 * save, not just when something looks suspicious.
 *
 * @param {string} clientId - the client being saved, excluded from the search.
 * @param {string[]} emails - addresses about to be claimed.
 * @returns {Promise<Array<{email: string, ownerId: string, ownerName: string}>>}
 */
export const findStaffEmailConflicts = async (clientId, emails) => {
  const wanted = new Set((emails || []).map((value) => String(value || '').trim().toLowerCase()).filter(Boolean));
  if (wanted.size === 0) return [];

  const snapshot = await get(dbRef(db, POS_CLIENTS_NODE));
  const conflicts = [];
  snapshot.forEach((child) => {
    if (child.key === clientId) return;
    const value = child.val() || {};
    Object.values(value.staff || {}).forEach((entry) => {
      const address = String(entry?.email || '').trim().toLowerCase();
      if (address && wanted.has(address)) {
        conflicts.push({ email: address, ownerId: child.key, ownerName: value.profile?.name || child.key });
      }
    });
  });
  return conflicts;
};

/**
 * Replaces one client's staff list.
 *
 * Validated in full BEFORE anything is written: a partial save would leave the
 * list in a state the administrator never chose, and half a staff list is an
 * access-control decision nobody made.
 *
 * The address is stored as a field as well as being the key, because the key
 * is a lossy form of it (dots become commas) and the admin screen has to show
 * the real thing back.
 *
 * Each entry carries the ROLE that account holds at this client, or '' for
 * none. The role is validated against the client's own roles, so a staff
 * record can never point at a role that was renamed away or deleted — which
 * would otherwise read at the till as "no permissions" with nothing on screen
 * to explain why.
 *
 * @param {string} clientId
 * @param {Array<{email: string, roleId?: string}|string>} staffList - blank
 *   entries are ignored, not rejected: the list editor always keeps one empty
 *   row for typing into. A plain string is accepted as an address with no
 *   role, which is what every record written before roles existed looks like.
 * @returns {Promise<Array<{email: string, roleId: string}>>} what was saved
 * @throws {Error} user-facing, on a malformed address, an unknown role, or an
 *   address already registered to another client.
 */
export const savePosClientStaff = async (clientId, staffList) => {
  const cleaned = (staffList || [])
    .map((entry) => (typeof entry === 'string' ? { email: entry } : (entry || {})))
    .map((entry) => ({ email: String(entry.email || '').trim(), roleId: String(entry.roleId || '').trim() }))
    .filter((entry) => entry.email)
    .map((entry) => ({ ...entry, email: validateStaffEmail(clientId, entry.email) }));

  // Deduplicated within this client too — the same address twice is not an
  // error worth stopping for, it just becomes one key. The LAST entry wins, so
  // editing the lower of two duplicate rows does what it looks like it does.
  const byEmail = new Map();
  cleaned.forEach((entry) => byEmail.set(entry.email, entry));
  const unique = [...byEmail.values()];

  // Checked against the roles as they stand AFTER this save — the form writes
  // roles first, so an address can be assigned a role created in the same
  // sitting.
  //
  // A failed roles read skips the check rather than failing the save: the
  // alternative is that a client whose roles cannot be read also cannot have
  // its staff list edited, which would turn one permission problem into two.
  let knownRoleIds = null;
  try {
    knownRoleIds = new Set((await fetchClientRoles(clientId)).map((role) => role.id));
  } catch (error) {
    console.error('Could not read roles to validate staff assignments:', error);
  }
  if (knownRoleIds) {
    const unknown = unique.filter((entry) => entry.roleId && !knownRoleIds.has(entry.roleId));
    if (unknown.length > 0) {
      throw userFacingError(
        `${unknown.map((entry) => entry.email).join(', ')} ${unknown.length === 1 ? 'is' : 'are'} assigned to a role that no longer exists. Pick a role from the list, or leave it unassigned.`,
      );
    }
  }

  const conflicts = await findStaffEmailConflicts(clientId, unique.map((entry) => entry.email));
  if (conflicts.length > 0) {
    const lines = conflicts.map(({ email, ownerName }) => `${email} — already staff of ${ownerName}`);
    throw userFacingError(
      `${conflicts.length === 1 ? 'This address is' : 'These addresses are'} already registered to another client:\n${lines.join('\n')}`,
    );
  }

  const staff = {};
  unique.forEach(({ email, roleId }) => {
    const key = emailKey(email);
    // roleId is omitted rather than stored blank: Firebase drops null keys, so
    // "no role" is the absence of the field and there is only one way to spell
    // it.
    if (key) staff[key] = roleId ? { email, roleId } : { email };
  });

  await set(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/staff`), staff);
  return unique;
};

/**
 * Whether this address may open this client's till.
 *
 * Reads ONE key rather than the list, which is what lets a member of staff
 * answer the question about themselves without being able to download every
 * colleague's address.
 *
 * Only a SUPERUSER passes without being listed (see POS_SUPERUSER_EMAILS).
 * An ordinary administrator gets no special treatment here: to work a
 * client's till they must appear on that client's staff list like anyone
 * else, so the audit trail always names someone who was actually meant to be
 * standing there.
 *
 * @param {string} clientId
 * @param {string} email
 * @returns {Promise<boolean>}
 */
export const isStaffOfClient = async (clientId, email) => {
  if (isPosSuperuser(email)) return true;
  const key = emailKey(email);
  if (!key) return false;
  try {
    const snapshot = await get(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/staff/${key}`));
    return snapshot.exists();
  } catch (error) {
    // A denied read is a "no" the caller can act on, not a crash. Logged so a
    // misconfigured rule is visible rather than looking like a wrong password.
    console.error('POS staff membership check failed:', error);
    return false;
  }
};

// ---- Global config --------------------------------------------------------

/**
 * Settings that apply to every client, with the defaults a fresh database
 * behaves by. Read by every till; written only from /admin.
 */
export const DEFAULT_GLOBAL_CONFIG = {
  // Printed in front of every amount. Global rather than per-client because
  // one deployment bills in one currency; a client-level override can be added
  // later without moving this.
  currencySymbol: '₱',
  currencyDescription: 'Philippine Peso',
  // Whether an idle till signs itself out after ADMIN_IDLE_TIMEOUT_MS.
  adminIdleTimeoutEnabled: true,
  // The line every receipt carries above the total.
  paymentNote: 'Only tap Confirm once the customer has paid.',
  // At or below this, a product is flagged as running low on both the till and
  // the Inventory screen. SUPERUSER-ONLY to change — see saveGlobalConfig and
  // the matching rule in database.rules.json.
  lowStockThreshold: POS_LOW_STOCK_DEFAULT,
};

/**
 * Live global config, merged over the defaults.
 *
 * A missing node is normal — it means nobody has opened Config yet — so the
 * defaults stand rather than an error being reported.
 *
 * @param {(config: object) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeGlobalConfig = (callback, onError) => onValue(
  dbRef(db, POS_GLOBAL_CONFIG_NODE),
  (snapshot) => callback({ ...DEFAULT_GLOBAL_CONFIG, ...(snapshot.val() || {}) }),
  (error) => {
    // Same distinction as the profile listener above: a denied read during a
    // sign-out must not be reported as "the club is on the default currency".
    // The caller keeps whatever it already had and re-subscribes.
    console.error('POS global config listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * Saves the global config. Admin-only in the rules.
 *
 * @param {object} config
 * @returns {Promise<void>}
 */
export const saveGlobalConfig = async (config, actorEmail = '') => {
  const clean = {
    currencySymbol: String(config?.currencySymbol || '').trim() || DEFAULT_GLOBAL_CONFIG.currencySymbol,
    currencyDescription: String(config?.currencyDescription || '').trim(),
    adminIdleTimeoutEnabled: config?.adminIdleTimeoutEnabled !== false,
    paymentNote: String(config?.paymentNote || '').trim(),
  };

  // THE THRESHOLD IS ONLY SENT BY A SUPERUSER, because only a superuser may
  // write it — the rule on that one key says so. An ordinary administrator
  // sending it would have the whole save refused for a field they never
  // touched, so it is left out of their payload entirely rather than included
  // and hoped for.
  if (isPosSuperuser(actorEmail)) {
    const threshold = Number(config?.lowStockThreshold);
    if (!Number.isInteger(threshold) || threshold < 0 || threshold > POS_LOW_STOCK_MAX) {
      throw userFacingError(`Enter the low-stock level as a whole number between 0 and ${POS_LOW_STOCK_MAX}. Use 0 to turn the warning off.`);
    }
    clean.lowStockThreshold = threshold;
  }

  // update(), not set(): set would need write permission on the whole node,
  // which an administrator no longer has now that one key is restricted — and
  // it would wipe the threshold every time an administrator saved.
  await update(dbRef(db, POS_GLOBAL_CONFIG_NODE), clean);
};

// ---- Roles ----------------------------------------------------------------
//
// A role is a name and a set of permission keys, stored per client at
// posClients/<clientId>/roles/<roleId>. Kept beside the staff list rather than
// in posTenants because it describes who may do what, which is the registry's
// business — and because only an administrator may write it.
//
// The id is derived from the name once, at creation, and then never changes:
// it is what a staff record will point at, so renaming "Cashier" to "Counter
// staff" must not orphan everyone who holds it.

/**
 * A storage id for a role name.
 *
 * Lower-case, hyphenated, and free of the characters RTDB forbids in a key.
 * Falls back to a timestamp for a name that reduces to nothing — an id is
 * required, and a role called "***" is still a role someone meant to create.
 *
 * @param {string} name
 * @returns {string}
 */
export const roleIdFromName = (name) => String(name || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, POS_ROLE_NAME_MAX_LENGTH) || `role-${Date.now()}`;

/**
 * Checks one role before it is saved.
 *
 * Permissions are filtered against POS_PERMISSION_KEYS rather than trusted:
 * a key that no longer exists in the app would sit in the database for ever,
 * granting nothing and confusing whoever reads it next.
 *
 * A STORE ADMIN role (`isAdmin`) is the club's own administrator: it has full
 * access at the till, may filter sales by staff, and may adjust the rights of
 * the club's other roles. Its permission list is therefore not stored — it
 * would be ignored.
 *
 * @param {{id?: string, name: string, permissions?: string[], isAdmin?: boolean}} role
 * @param {object[]} [existingRoles] - the other roles, for the duplicate check.
 * @returns {{id: string, name: string, permissions: string[], isAdmin: boolean}}
 * @throws {Error} user-facing, naming the specific problem
 */
export const validateRole = (role, existingRoles = []) => {
  const name = String(role?.name || '').trim();
  if (!name) throw userFacingError('Give the role a name.');
  if (name.length > POS_ROLE_NAME_MAX_LENGTH) {
    throw userFacingError(`A role name can be at most ${POS_ROLE_NAME_MAX_LENGTH} characters.`);
  }

  // An id ALREADY ON the role means an existing role is being edited, and only
  // then is a match on that id itself rather than a conflict.
  //
  // That distinction is the whole check. Deriving the id first and then
  // excusing every role sharing it would make a NEW role called "cashier"
  // indistinguishable from an existing "Cashier": no clash reported, the same
  // storage key, and the existing role silently replaced by the new one.
  const isEdit = Boolean(role?.id);
  const id = isEdit ? role.id : roleIdFromName(name);
  const others = isEdit ? existingRoles.filter((other) => other.id !== id) : existingRoles;

  // Compared case-insensitively: "Cashier" and "cashier" would be two roles
  // nobody could tell apart in the list they are picked from.
  const nameClash = others.some((other) => String(other.name || '').trim().toLowerCase() === name.toLowerCase());
  if (nameClash) throw userFacingError(`"${name}" is already a role for this client.`);

  // Two different names can still reduce to one id — "Cashier" and "Cashier!"
  // both give `cashier`. Refused rather than quietly merged, because the id is
  // what a staff record will point at.
  if (others.some((other) => other.id === id)) {
    throw userFacingError(`"${name}" is too close to the name of an existing role. Please make it more distinct.`);
  }

  const isAdmin = role?.isAdmin === true;
  const permissions = isAdmin ? [] : (role?.permissions || []).filter((key) => POS_PERMISSION_KEYS.includes(key));
  // A role granting nothing is allowed — it is a useful way to park someone
  // without deleting them — but it is worth being deliberate about, so the
  // form warns rather than this refusing.

  return { id, name, permissions, isAdmin };
};

/**
 * Every role defined for one client, sorted by name.
 *
 * @param {string} clientId
 * @returns {Promise<Array<{id: string, name: string, permissions: string[]}>>}
 */
export const fetchClientRoles = async (clientId) => {
  const snapshot = await get(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/${POS_ROLES_NODE}`));
  const value = snapshot.val() || {};
  return Object.entries(value)
    .map(([id, role]) => ({
      id,
      name: String(role?.name || id),
      isAdmin: role?.isAdmin === true,
      // Stored as { key: true } rather than an array: Firebase turns a sparse
      // array into an object anyway, and a map cannot end up with holes in it.
      permissions: Object.keys(role?.permissions || {}).filter((key) => POS_PERMISSION_KEYS.includes(key)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Replaces one client's roles.
 *
 * Validated in full before anything is written, for the same reason the staff
 * list is: half a set of roles is an access decision nobody made.
 *
 * @param {string} clientId
 * @param {Array<{id?: string, name: string, permissions?: string[]}>} roles
 * @returns {Promise<object[]>} the roles actually saved
 * @throws {Error} user-facing, on a blank or duplicate name
 */
export const savePosClientRoles = async (clientId, roles) => {
  const validated = [];
  (roles || []).forEach((role) => {
    validated.push(validateRole(role, validated));
  });

  const payload = {};
  validated.forEach(({ id, name, permissions, isAdmin }) => {
    payload[id] = {
      name,
      // Written only when true, so "not an admin role" has one spelling.
      ...(isAdmin ? { isAdmin: true } : {}),
      permissions: permissions.reduce((map, key) => ({ ...map, [key]: true }), {}),
    };
  });

  await set(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/${POS_ROLES_NODE}`), payload);
  return validated;
};

/**
 * What the person at this till may do.
 *
 * Resolved from TWO reads a member of staff is allowed to make: their own
 * staff record (one key, not the list) and the client's roles. Neither is the
 * admin-only whole-registry read, so this works for an ordinary cashier.
 *
 * `unrestricted` is a separate flag rather than "holds every permission",
 * because the two mean different things to whoever reads it next: a superuser
 * and an account whose role happens to tick all seven boxes should not be
 * indistinguishable.
 *
 * WHAT THIS IS AND IS NOT. It decides what the SCREEN offers. The database
 * rules still let any signed-in account write any client's products and
 * orders (see the honest-limit note in database.rules.json), so a role keeps
 * an untrained cashier out of the sales figures — it is not a defence against
 * someone calling the database directly. Closing that needs uid-keyed rules
 * or a custom claim, the same work the tenant boundary is waiting on.
 *
 * A FAILED READ GRANTS NOTHING. Returning "allow everything" when the lookup
 * breaks would make a rules problem look like a promotion.
 *
 * THE CLUB'S OWN RIGHTS WIN over the administrator's defaults. A store admin
 * may re-tick what a role allows (Config -> Staff Access); that is stored in
 * the club's subtree as an override, and applies to this club only.
 *
 * `isStoreAdmin` is true for a role the administrator marked as the store's
 * admin, for a superuser, and for an account with no role (which keeps full
 * access — see POS_UNASSIGNED_STAFF_UNRESTRICTED).
 *
 * @param {string} clientId
 * @param {string} email - the address the till verified.
 * @returns {Promise<{isStaff: boolean, unrestricted: boolean, isStoreAdmin: boolean, roleId: string, roleName: string, permissions: string[]}>}
 */
export const fetchStaffPermissions = async (clientId, email) => {
  const none = { isStaff: false, unrestricted: false, isStoreAdmin: false, roleId: '', roleName: '', permissions: [] };

  // A superuser is not on the staff list at all, so there is no record to read
  // and nothing to resolve: they hold the client open, not a job at it.
  if (isPosSuperuser(email)) {
    return { ...none, isStaff: true, unrestricted: true, isStoreAdmin: true, roleName: 'Superuser' };
  }

  const key = emailKey(email);
  if (!key) return none;

  try {
    const snapshot = await get(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/staff/${key}`));
    // Not on the list is reported as isStaff:false rather than thrown, so the
    // caller can tell "no longer works here" from "works here, allowed
    // nothing" — two different things to say to whoever is at the counter.
    if (!snapshot.exists()) return none;

    const roleId = String(snapshot.val()?.roleId || '').trim();
    if (!roleId) {
      return {
        ...none,
        isStaff: true,
        unrestricted: POS_UNASSIGNED_STAFF_UNRESTRICTED,
        isStoreAdmin: POS_UNASSIGNED_STAFF_UNRESTRICTED,
      };
    }

    const roleSnapshot = await get(dbRef(db, `${POS_CLIENTS_NODE}/${clientId}/${POS_ROLES_NODE}/${roleId}`));
    const role = roleSnapshot.val();
    // A role deleted since it was assigned grants nothing, rather than falling
    // back to unassigned: the administrator removed it, and silently restoring
    // FULL access would be the exact opposite of what they did. The admin
    // screen refuses to delete a role anyone holds, so this is the last line
    // rather than the expected path.
    if (!role) return { ...none, isStaff: true, roleId };

    if (isStoreAdminRole(role)) {
      return { ...none, isStaff: true, unrestricted: true, isStoreAdmin: true, roleId, roleName: String(role.name || roleId) };
    }

    // The club's own override, if its store admin has changed this role.
    // A failed read falls back to the default rather than to nothing: the
    // default is what an administrator chose, so it is never a promotion.
    let rights = role.permissions || {};
    try {
      const overrideSnap = await get(dbRef(db, `${POS_TENANTS_NODE}/${clientId}/${POS_ROLE_OVERRIDES_NODE}/${roleId}`));
      if (overrideSnap.exists()) rights = overrideSnap.val()?.permissions || {};
    } catch (overrideError) {
      console.error('Could not read role override; using the default:', overrideError);
    }

    return {
      isStaff: true,
      unrestricted: false,
      isStoreAdmin: false,
      roleId,
      roleName: String(role.name || roleId),
      permissions: Object.keys(rights).filter((permission) => POS_PERMISSION_KEYS.includes(permission)),
    };
  } catch (error) {
    console.error('Could not resolve staff permissions:', error);
    return none;
  }
};
